/* eslint-disable max-lines */
// deps
import React, { ReactElement, memo, useState, useMemo, useContext, useCallback } from "react";

// internal-deps
import { CardDetails, VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";
import { JuspayTokenizationMeta } from "@swiggy-private/payment-types";
import { GenericError, DeleteIcon as DeleteCardIcon, useEffectOnce } from "@swiggy-private/core-ui";
import {
    ManageCard as ManageCardUI,
    LoadingScreen,
    PaymentProgressLoader,
    PaymentAlert,
} from "@swiggy-private/payment-ui";
import {
    ApiContext,
    CheckoutRoute,
    getBridgeSdk,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    useNavigate,
    useSessionStorage,
} from "@swiggy-private/checkout-core";
import { AnalyticsContext, PublicApiContext, CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS } from "@swiggy-private/checkout-core";

// components
import { DeleteCardAlert } from "./DeleteCardAlert";
import PaymentModesWrapper from "../payment-modes-wrapper";

// contexts
import { PaymentModesContext } from "@payment-client/contexts/PaymentModesContext";

//interface
import { RouteProps } from "@payment-client/interfaces/Router";

//styles
import styles from "./styles.scss";

// helpers
import { getSelectedCardDetails } from "@payment-client/helpers/ManageCard";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

// constants
import { HEADER_TITLE } from "@payment-client/constants/Header";
import { PAYMENT_MODES_STORAGE_KEYS } from "@payment-client/constants/PaymentModes";
import { getPaymentErrorScreenAction } from "@payment-client/helpers/Misc";
import { PAYMENT_INITIAL_STATE } from "@payment-client/components/payment-main";
import { API_SUCCESS_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";

// Analytics
import { onDeleteCardClickEvent } from "@payment-client/analytics/events/DeleteCard";
import {
    onManageCardScreenViewEvent,
    onSaveCardClickEvent,
    onSecureCardClickEvent,
} from "@payment-client/analytics/events/Card";
import { updateCard } from "@payment-client/apis/Card";
import { useCardHandlerPaymentModes } from "@payment-client/components/payment-modes/hooks/UseCardHandlerPaymentModes";
import { SCREEN_NAME } from "@payment-client/analytics/constants";
import { updateCardPublic } from "@payment-client/apis/publicCard";
import { CARD_API_KEYS } from "@swiggy-private/checkout-core";

const TICK_ICON = "PaymentLogos/paymentIcons/tick";
const ERROR_ICON = "PaymentLogos/paymentIcons/error";
const TEXTS = {
    CARD_UPDATED_SUCCESS_TITLE: "Card Details Updated",
    CARD_UPDATED_SUCCESS_MESSAGE: "Your card detail has been updated successfully.",
    CARD_UPDATED_ERROR_TITLE: `Couldn't Update Card Detail`,
    CARD_UPDATED_ERROR_MESSAGE: `Sorry we are unable to update your card details at the moment.Please try again later.`,
    DISMISS: "DISMISS",
};

type ManageCardPaymentProps = RouteProps;

interface ManageCardDetails {
    cardDetails: CardDetails;
    userConsent: boolean;
}

interface CardDetailiUpdationState {
    showPrompt: boolean;
    success: boolean;
    message: string | null | undefined;
    isUpdateInProgress: boolean;
}

interface ManageCardContentProps {
    onClickSave: (nickName: string, userConsent: boolean) => void;
    cardDetails: CardDetails;
    juspayTokenizationMeta?: JuspayTokenizationMeta;
    userConsent?: boolean;
    cardDetailUpdation: CardDetailiUpdationState;
    resetCardUpdationDetail: () => void;
}

const ManageCardContent = memo(function ManageCardContent(props: ManageCardContentProps): ReactElement {
    const {
        onClickSave,
        cardDetails,
        juspayTokenizationMeta,
        userConsent,
        cardDetailUpdation,
        resetCardUpdationDetail,
    } = props;
    const [isDeleteCardAlertActive, setDeleteCardAlert] = useState(false);

    const { clickEvent, screenViewEvent } = React.useContext(AnalyticsContext);

    const { onKnowMoreTokenizationClickAction } = useCardHandlerPaymentModes(
        SCREEN_NAME.MANAGE_CARD_PAYMENT_MODES,
    );

    const onDeleteIconClick = useCallback(() => {
        setDeleteCardAlert(true);
        //Analytics
        onDeleteCardClickEvent(clickEvent);
    }, [clickEvent]);

    const resetDeleteCardAlert = useCallback(() => {
        setDeleteCardAlert(false);
    }, []);

    //Delete Card Icon
    const deleteCardIcon = useMemo(
        () => (
            <div className={styles.iconContainer}>
                <div className={styles.icon} onClick={onDeleteIconClick}>
                    <DeleteCardIcon />
                </div>
            </div>
        ),
        [onDeleteIconClick],
    );

    useEffectOnce(() => {
        getBridgeSdk()
            .setScreenCaptureState({ screenCapturingDisabled: true })
            .catch(() => void 0);

        onManageCardScreenViewEvent(screenViewEvent);
        recordNREvent({
            eventType: NEWRELIC_TABLES.PAYMENT_MODES_MANAGE_CARD,
        });
    });

    return (
        <PaymentModesWrapper
            title={HEADER_TITLE.MANAGE_CARD}
            headerExtraContent={deleteCardIcon}
            containerClassName={styles.container}
        >
            <ManageCardUI
                cardDetails={cardDetails}
                onClickSave={onClickSave}
                juspayTokenizationMeta={juspayTokenizationMeta}
                userConsent={userConsent}
                onKnowMoreTokenizationClickAction={onKnowMoreTokenizationClickAction}
            />
            {isDeleteCardAlertActive && (
                <DeleteCardAlert
                    resetDeleteCardAlert={resetDeleteCardAlert}
                    cardDetails={cardDetails}
                />
            )}
            {cardDetailUpdation.isUpdateInProgress && <PaymentProgressLoader />}
            {cardDetailUpdation.showPrompt && (
                <PaymentAlert
                    title={
                        cardDetailUpdation.success ? TEXTS.CARD_UPDATED_SUCCESS_TITLE : TEXTS.CARD_UPDATED_ERROR_TITLE
                    }
                    message={
                        cardDetailUpdation.success
                            ? TEXTS.CARD_UPDATED_SUCCESS_MESSAGE
                            : TEXTS.CARD_UPDATED_ERROR_MESSAGE
                    }
                    icon={cardDetailUpdation.success ? TICK_ICON : ERROR_ICON}
                    hideOnOverlayClick={true}
                    onClose={resetCardUpdationDetail}
                    primaryAction={{
                        text: TEXTS.DISMISS,
                        onClick: resetCardUpdationDetail,
                    }}
                />
            )}
        </PaymentModesWrapper>
    );
});

export const ManageCard = memo(function ManageCard(_props: ManageCardPaymentProps): ReactElement | null {
    const navigateTo = useNavigate();

    const { getAllCardsResponse, loadingPaymentModes } = useContext(PaymentModesContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const apiContextValue = useContext(ApiContext);
    const apiHost = apiContextValue.host.unwrap();

    const publicApiConfig = useContext(PublicApiContext);

    const isPublicApiEnable = useMemo(
        () =>
            isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.CARDS_JUSPAY_APIS,
                CARD_API_KEYS.UPDATE_CARD,
            ),
        [publicApiConfig],
    );

    const [manageCardDetails, setManageCardDetails] = useSessionStorage<ManageCardDetails | undefined>(
        PAYMENT_MODES_STORAGE_KEYS.MANAGE_CARD,
        undefined,
    );

    const [cardDetailUpdation, setCardDetailsUpdation] = useState<CardDetailiUpdationState>({
        isUpdateInProgress: false,
        showPrompt: false,
        success: false,
        message: "",
    });

    const resetCardUpdationDetail = useCallback(() => {
        setCardDetailsUpdation({
            showPrompt: false,
            success: false,
            message: "",
            isUpdateInProgress: false,
        });
    }, []);

    const cardDetails = useMemo(() => {
        if (loadingPaymentModes) {
            return;
        }
        // to persist details on coming back or through redirect
        if (manageCardDetails != null) {
            return manageCardDetails.cardDetails;
        }
        const cardFingerprint = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cardFingerprint);
        const cardsList = getAllCardsResponse?.cards || [];

        return getSelectedCardDetails(cardFingerprint, cardsList);
    }, [getAllCardsResponse, loadingPaymentModes, manageCardDetails]);

    const handleUpdateNickName = useCallback(
        (nickName: string) => {
            if (!cardDetails) {
                return;
            }

            const updateCardFn = isPublicApiEnable ? updateCardPublic : updateCard;

            updateCardFn(apiHost, {
                cardRef: cardDetails.cardReference,
                customerId: GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId),
                nickName: nickName,
                nameOnCard: cardDetails.nameOnCard,
            })
                .then(res => {
                    if (res.statusCode === API_SUCCESS_STATUS_CODE) {
                        setCardDetailsUpdation({
                            showPrompt: true,
                            message: res.statusMessage,
                            success: true,
                            isUpdateInProgress: false,
                        });
                    } else {
                        setCardDetailsUpdation({
                            showPrompt: true,
                            message: res.statusMessage,
                            success: false,
                            isUpdateInProgress: false,
                        });
                    }
                })
                .catch(() => {
                    setCardDetailsUpdation({
                        showPrompt: true,
                        message: OOPS_SOMETHING_WENT_WRONG,
                        success: false,
                        isUpdateInProgress: false,
                    });
                });
        },
        [apiHost, cardDetails, isPublicApiEnable],
    );

    const onClickSave = useCallback(
        (nickName: string, userConsent: boolean): void => {
            if (userConsent && cardDetails && !cardDetails.isSecured && cardDetails.tokenizeSupport) {
                onSecureCardClickEvent(clickEvent);
                setManageCardDetails({
                    cardDetails: { ...cardDetails, nickname: nickName },
                    userConsent,
                });
                navigateTo(CheckoutRoute.SECURE_CARD, {
                    savedCardDetails: { nickName },
                    paymentMethod: VALID_PAYMENT_GROUP_TYPES.CARD,
                });
                return;
            } else {
                onSaveCardClickEvent(clickEvent);
                if (!cardDetails) {
                    return;
                }
                setCardDetailsUpdation({
                    ...cardDetailUpdation,
                    isUpdateInProgress: true,
                });
                handleUpdateNickName(nickName);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cardDetailUpdation, cardDetails, clickEvent, handleUpdateNickName, navigateTo],
    );

    if (loadingPaymentModes) {
        return (
            <PaymentModesWrapper title={HEADER_TITLE.MANAGE_CARD}>
                <LoadingScreen />
            </PaymentModesWrapper>
        );
    }

    if (!getAllCardsResponse || !cardDetails) {
        const action = getPaymentErrorScreenAction(PAYMENT_INITIAL_STATE.MANAGE_CARD);

        return (
            <PaymentModesWrapper title={HEADER_TITLE.MANAGE_CARD}>
                <GenericError action={action} />
            </PaymentModesWrapper>
        );
    }

    return (
        <ManageCardContent
            onClickSave={onClickSave}
            cardDetails={cardDetails}
            juspayTokenizationMeta={getAllCardsResponse.meta.tokenizationMeta}
            userConsent={manageCardDetails?.userConsent}
            cardDetailUpdation={cardDetailUpdation}
            resetCardUpdationDetail={resetCardUpdationDetail}
        />
    );
});
